import { FC, useEffect, useRef, useState } from "react";
import {
  MuiBox,
  WarningConfirmationModal,
} from "../../../../../../../../components";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { ProgramTemplateWrapper } from "./FeeDetails.style";

import {
  GetProgramFeeResponse,
  IdNameList,
} from "../../../../../../../../api/services/common/models";
import { BillingTypeConfigureTable } from "../../../../../../../../components/billingTypeConfigureTable/BillingTypeConfigureTable";
import { BillingConfigurationDetailsFormKeyProps } from "../../../../../../../../components/billingTypeConfigureTable/modelsAndSchemas/Models";
import { CurrencyFormat } from "../../../../../../../../components/textFieldCurrency/CurrencyUtils";
import { ProgramDetail } from "../../../../../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { BillingTypeFieldInvoice } from "./billingTypeFieldInvoice/BillingTypeFieldInvoice";
import { getValueOrBlank } from "../../../../../../../../helpers/utils";

interface FeeDetailsProps {
  pageId: string;
  programData?: ProgramDetail;
  isEditProgram?: boolean;
  currencyFormat: CurrencyFormat;
  currencyId: string;
  defaultUserCurrency: string;
  currenciesList: IdNameList[];
  currencyName: string;
  forceUpdate: () => void;
  billingConfigurationDetailsFormKeys: BillingConfigurationDetailsFormKeyProps;
  programFeeDetailsData?: GetProgramFeeResponse;
  resetData?: boolean;
}

const FeeDetails: FC<FeeDetailsProps> = ({
  pageId,
  programData,
  isEditProgram,
  currencyFormat,
  currencyId,
  defaultUserCurrency,
  currenciesList,
  currencyName,
  forceUpdate,
  billingConfigurationDetailsFormKeys,
  programFeeDetailsData,
  resetData,
}) => {
  const billingTypeFieldInvoiceRef = useRef<{
    handleConfirmationStatusTrue: () => void;
    handleConfirmationStatusFalse: () => void;
  }>(null);
  const billingTypeConfigureTableRef = useRef<{
    resetData: () => void;
  }>(null);

  const [showWarningOnBillingChange, setShowWarningOnBillingChange] =
    useState(false);

  const { fetchLabel, commonLabels } = useSiteCore();

  const { programFeeTitle, areYouSureYouWantToChangeTheBillingType } =
    programSiteCoreKeys;

  const handleModalClose = () => {
    setShowWarningOnBillingChange(false);
    billingTypeFieldInvoiceRef.current?.handleConfirmationStatusFalse();
  };

  const handleBillingChange = () => {
    billingTypeFieldInvoiceRef.current?.handleConfirmationStatusTrue();
    setShowWarningOnBillingChange(false);
    setTimeout(() => {
      forceUpdate();
    }, 200);
  };

  useEffect(() => {
    billingTypeConfigureTableRef.current?.resetData();
  }, [resetData]);

  return (
    <ProgramTemplateWrapper>
      <form>
        <MuiBox>
          <MuiBox className="program-template-wrapper">
            <MuiBox className="program-template">
              <BillingTypeFieldInvoice
                ref={billingTypeFieldInvoiceRef}
                currencyFormat={currencyFormat}
                currencyId={currencyId}
                defaultUserCurrency={defaultUserCurrency}
                currenciesList={currenciesList || []}
                forceUpdate={forceUpdate}
                programTemplateId={getValueOrBlank(programData?.programTemplateId)}
                pageId={pageId}
                setShowWarningOnBillingChange={setShowWarningOnBillingChange}
                programData={programData}
                programFeeDetailsData={programFeeDetailsData}
              />
              <BillingTypeConfigureTable
                ref={billingTypeConfigureTableRef}
                pageId={pageId}
                currencyName={currencyName}
                currencyFormat={currencyFormat}
                currencyId={currencyId}
                forceUpdate={forceUpdate}
                programTemplateId={getValueOrBlank(programData?.programTemplateId)}
                isInEditMode={isEditProgram}
                isFromBillingInvoice={true}
                billingConfigurationDetailsFormKeys={
                  billingConfigurationDetailsFormKeys
                }
                clientId={programData?.clientId!}
              />
            </MuiBox>
          </MuiBox>
        </MuiBox>

        {showWarningOnBillingChange && (
          <WarningConfirmationModal
            show={showWarningOnBillingChange}
            modalTitle={commonLabels.confirmation}
            onClose={handleModalClose}
            onPrimaryButtonClick={handleBillingChange}
            secondaryButtonTitle={commonLabels.no}
            labelContent={fetchLabel(
              programFeeTitle,
              areYouSureYouWantToChangeTheBillingType,
            )}
          />
        )}
      </form>
    </ProgramTemplateWrapper>
  );
};

export default FeeDetails;
